import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';

// Create a context for authentication
const AuthContext = createContext(null);

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  // State variables
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Set up axios interceptor for authentication
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      config => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      error => Promise.reject(error)
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
    };
  }, []);

  // Check authentication status on initial load
  useEffect(() => {
    const checkAuthStatus = async () => {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get('users/profile');
        setCurrentUser(response.data.user);
      } catch (err) {
        console.error('Auth check failed:', err);
        localStorage.removeItem('token');
      } finally {
        setLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  // Login function
  const login = async (email, password) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.post('users/login', { email, password });
      const { token, user } = response.data;
      
      localStorage.setItem('token', token);
      setCurrentUser(user);
      
      return user;
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Login failed. Please try again.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('token');
    setCurrentUser(null);
  };

  // Register function
  const register = async (email, password, name, recaptchaToken) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.post('users/register', { 
        email, 
        password, 
        name,
        recaptchaToken 
      });
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Registration failed. Please try again.';
      setError(errorMessage);
      // Preserve the original error with response data
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update profile function
  const updateProfile = async (userData) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.put('users/update', userData);
      setCurrentUser(response.data.user);
      
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Profile update failed. Please try again.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Get user bookings function
  const getUserBookings = async () => {
    try {
      if (!currentUser) throw new Error('User not authenticated');
      
      const response = await axios.get('users/bookings');
      return response.data.bookings;
    } catch (err) {
      console.error('Get bookings error:', err);
      
      // Handle 401 errors by logging out
      if (err.response?.status === 401) {
        logout();
      }
      
      return [];
    }
  };

  // Add a function to book a class
  const bookClass = async (bookingData) => {
    try {
      setLoading(true);
      setError(null);
      
      // Make booking request - use the new endpoint that supports guest bookings
      const response = await axios.post('bookings/create', bookingData);
      
      // Return booking details
      return response.data;
    } catch (err) {
      console.error('Booking error:', err.response?.data || err);
      
      const errorMessage = err.response?.data?.error?.message || err.response?.data?.error || 'Booking failed. Please try again.';
      setError(errorMessage);
      
      // Pass the full error object with response data
      if (err.response) {
        err.message = errorMessage;
        throw err;
      } else {
        throw new Error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // Add a function to claim a guest account
  const claimGuestAccount = async (guestToken, password) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.post('bookings/guest-claim', {
        guest_token: guestToken,
        password
      });
      
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.error?.message || err.response?.data?.error || 'Failed to claim account. Please try again.';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Provide the auth context value
  const value = {
    currentUser,
    loading,
    error,
    login,
    logout,
    register,
    updateProfile,
    getUserBookings,
    bookClass,
    claimGuestAccount,
    isAdmin: () => currentUser && currentUser.is_admin === true
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
